import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCrmImpersonation } from "@/contexts/CrmImpersonationContext";
import { UserSearch } from "lucide-react";
import { SelectorVerComo } from "@/components/ui/selector-ver-como";

/**
 * Selector "Ver como" para el Portal CRM. Permite a un Super Admin (rol 1 o 2)
 * impersonar a cualquier usuario con acceso al portal CRM. La lista se calcula
 * a partir de submenus_permisos (cualquier submenu cuya ruta empieza con
 * /admin/portal-crm/...). Si aún no hay submenús cargados en BD para CRM,
 * cae en un fallback que muestra los usuarios con rol Super Admin.
 *
 * Se pinta con el selector estándar de los portales (SelectorVerComo).
 */
export function CrmImpersonationSelector({ className, compacto }: { className?: string; compacto?: boolean }) {
  const { profile } = useAuth();
  const {
    impersonatedCrmUserEmail,
    impersonatedCrmUserName,
    setImpersonatedCrmUser,
    clearImpersonation,
    isImpersonating,
  } = useCrmImpersonation();

  const canImpersonate = profile?.puede_impersonar === true;

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["crm-users-for-impersonation"],
    queryFn: async () => {
      // 1) Roles que tienen al menos un permiso sobre rutas /admin/portal-crm/...
      const { data: subs, error: subsErr } = await supabase
        .from("submenus")
        .select("id, vista_front_end")
        .like("vista_front_end", "/admin/portal-crm/%");
      if (subsErr) throw subsErr;

      let rolIds: number[] = [];
      const submenuIds = (subs || []).map((s: any) => s.id);
      if (submenuIds.length > 0) {
        const { data: perms, error: permsErr } = await supabase
          .from("submenus_permisos")
          .select("rol_id")
          .in("submenu_id", submenuIds)
          .eq("activo", true);
        if (permsErr) throw permsErr;
        rolIds = Array.from(new Set((perms || []).map((p: any) => p.rol_id)));
      }
      // Fallback: si BD aún no tiene submenús/permisos del CRM, mostrar Super Admins.
      if (rolIds.length === 0) rolIds = [1];

      const { data: usuarios, error: usuariosErr } = await supabase
        .from("usuarios")
        .select("auth_user_id, email, nombre, rol_id, roles(nombre)")
        .in("rol_id", rolIds)
        .eq("activo", true)
        .order("email");
      if (usuariosErr) throw usuariosErr;

      return (usuarios || [])
        .map((u: any) => ({
          id: u.auth_user_id,
          email: u.email,
          nombre: u.nombre || u.email,
          rolId: u.rol_id,
          rolNombre: u.roles?.nombre || "Sin rol",
        }))
        .sort((a: any, b: any) => a.nombre.localeCompare(b.nombre));
    },
    enabled: canImpersonate,
  });

  // Después de los hooks: `profile` llega async, así que salir antes cambiaría
  // el orden de hooks entre renders.
  if (!canImpersonate) return null;

  const etiqueta = isImpersonating
    ? impersonatedCrmUserName || users.find((u: any) => u.email === impersonatedCrmUserEmail)?.nombre || "Usuario"
    : "Super Admin";

  return (
    <SelectorVerComo
      className={className}
      compacto={compacto}
      etiqueta={etiqueta}
      impersonando={isImpersonating}
      iconoVistaPropia={<UserSearch className="size-3.5" strokeWidth={1.75} />}
      opcionPorDefecto={{
        key: "__super_admin__",
        label: "Super Admin",
        nota: "Por defecto",
        seleccionada: !isImpersonating,
        onSelect: clearImpersonation,
        icono: <UserSearch className="mr-2 size-3.5 shrink-0 text-primary" />,
      }}
      grupos={[{
        titulo: "Usuarios del CRM",
        opciones: users.map((u: any) => ({
          key: u.email,
          label: u.nombre,
          nota: u.rolNombre,
          buscarPor: `${u.email} ${u.rolNombre}`,
          seleccionada: impersonatedCrmUserEmail === u.email,
          onSelect: () => setImpersonatedCrmUser(u.email, u.id, u.nombre, u.rolId),
        })),
      }]}
      onLimpiar={clearImpersonation}
      ariaLimpiar="Volver a Super Admin"
      textoVacio="Sin usuarios con acceso al CRM."
      cargando={isLoading}
    />
  );
}
